import { useEffect, useMemo, useRef, useState } from "react";

import GameCard from "@/components/game-card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { GameStatusEnum } from "@/pages/search/types";

import type { GameType } from "./types";

interface GameCardListProps {
  games: GameType[];
  showClosedRooms: boolean;
}

export function GameCardList({ games, showClosedRooms }: GameCardListProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!scrollContainerRef.current || games.length === 0) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setCurrentIndex(
              Number.parseInt(
                entry.target.getAttribute("data-index") || "0",
                10
              )
            );
          }
        }
      },
      {
        root: scrollContainerRef.current,
        threshold: 1,
      }
    );

    for (const ref of cardRefs.current) {
      if (ref) {
        observer.observe(ref);
      }
    }

    return () => observer.disconnect();
  }, [games]);

  const onSetCurrentIndex = (index: number) => {
    cardRefs.current[index]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  };

  const cards = useMemo(
    () =>
      games.map((game, index) =>
        game.status !== GameStatusEnum.OP && !showClosedRooms ? null : (
          <GameCard
            className="w-2/3 shrink-0 md:w-auto"
            data-index={index}
            game={game}
            key={game.id}
            ref={(el) => {
              cardRefs.current[index] = el || null;
            }}
          />
        )
      ),
    [games, showClosedRooms]
  );
  const ariaLabels = useMemo(
    () =>
      games.reduce((acc, game) => {
        if (game.status !== GameStatusEnum.OP && !showClosedRooms) {
          return acc;
        }
        acc.push(game.name);
        return acc;
      }, [] as string[]),

    [games, showClosedRooms]
  );

  return (
    <div>
      <div
        className="scrollbar-hidden -mx-6 flex snap-x snap-mandatory grid-cols-4 gap-[25px] overflow-x-auto px-6 pt-10 *:snap-center md:mx-0 md:grid md:px-0"
        ref={scrollContainerRef}
      >
        {cards}
      </div>
      <div className="mt-5 flex justify-center gap-2 md:hidden">
        {ariaLabels.map((ariaLabel: string, index: number) => (
          <Button
            aria-label={ariaLabel}
            className={cn(
              "size-2 rounded-full transition-[colors,shadow]",
              index === currentIndex
                ? "bg-rose-red shadow-0-3-5 shadow-dark-magenta/64"
                : "border border-white bg-transparent"
            )}
            // biome-ignore lint/suspicious/noArrayIndexKey: legacy
            key={index}
            onClick={() => onSetCurrentIndex(index)}
          />
        ))}
      </div>
    </div>
  );
}

interface GameCardSectionProps {
  actionButtons: React.ReactNode;
  games: GameType[];
  showClosedRooms: boolean;
  title: React.ReactNode;
}

export default function GameCardsSection({
  actionButtons,
  games,
  showClosedRooms,
  title,
}: GameCardSectionProps) {
  const hasJustClosedGames = !games.some(
    (game) => game.status === GameStatusEnum.OP
  );
  return (
    <>
      <div className="flex justify-between">
        <h3
          className={cn(
            "mt-10 mb-7.5 text-24 md:mt-17.5 md:text-32",
            hasJustClosedGames ? "text-pale-lavender" : "text-white"
          )}
        >
          {title}
        </h3>
        <div className="hidden justify-center md:flex">{actionButtons}</div>
      </div>
      <GameCardList games={games} showClosedRooms={showClosedRooms} />
      <div className="flex justify-center md:hidden">{actionButtons}</div>
    </>
  );
}
